import ReactQueryInteropReducer from 'src/queries/redux/reactQueryInterop.reducer'
import {
  GetCspMeSuccessAction,
  PostCspLoginIssuerInfoSuccessAction,
  PostCspLoginSuccessAction,
} from 'src/redux/actions/auth.actions'
import {
  RESET_DATA_STATE,
  ResetDataStateAction,
} from 'src/redux/actions/data.actions'
import ContactsReducer, {
  Actions as ContactsActions,
  ContactsState,
  initialState as initialContactsState,
} from 'src/redux/reducers/data/contacts.reducer'
import CreditCardsReducer, {
  Actions as CreditCardsActions,
  CreditCardsState,
  initialState as initialCreditCardsState,
} from 'src/redux/reducers/data/creditCards.reducer'
import ExpensesReducer, {
  Actions as ExpensesActions,
  ExpensesState,
  initialState as initialExpensesState,
} from 'src/redux/reducers/data/expenses.reducer'
import InvitedToOrgReducer, {
  Actions as InvitedToOrgActions,
  initialState as initialInvitedToOrgState,
  InvitedToOrgState,
} from 'src/redux/reducers/data/invitedToOrg.reducer'
import MembersReducer, {
  Actions as MembersActions,
  initialState as initialMembersState,
  MembersState,
} from 'src/redux/reducers/data/members.reducer'
import OrganizationsReducer, {
  Actions as OrganizationsActions,
  initialState as initialOrganizationsState,
  OrganizationsState,
} from 'src/redux/reducers/data/organizations.reducer'
import OrgIntegrationsReducer, {
  Actions as OrgIntegrationsActions,
  initialState as initialOrgIntegrationsState,
  OrgIntegrationsState,
} from 'src/redux/reducers/data/orgIntegrations.reducer'
import ReceiptAttachmentsReducer, {
  Actions as ReceiptAttachmentsActions,
  initialState as initialReceiptAttachmentsState,
  ReceiptAttachmentsState,
} from 'src/redux/reducers/data/receiptAttachments.reducer'
import ReportDownloadsReducer, {
  Actions as ReportDownloadsActions,
  initialState as initialReportDownloadsState,
  ReportDownloadsState,
} from 'src/redux/reducers/data/reportDownloads.reducer'
import StatisticsReducer, {
  Actions as StatisticsActions,
  initialState as initialStatisticsState,
  StatisticsState,
} from 'src/redux/reducers/data/statistics.reducer'
import TransactionsReducer, {
  Actions as TransactionsActions,
  initialState as initialTransactionsState,
  TransactionsState,
} from 'src/redux/reducers/data/transactions.reducer'
import UsersReducer, {
  Actions as UsersActions,
  initialState as initialUsersState,
  UsersState,
} from 'src/redux/reducers/data/users.reducer'
import VirtualCardPushTasksReducer, {
  Actions as VirtualCardPushTasksActions,
  initialState as initialVirtualCardPushTasksState,
  VirtualCardPushTasksState,
} from 'src/redux/reducers/data/virtualCardPushTasks.reducer'
import VirtualCardsReducer, {
  Actions as VirtualCardsActions,
  initialState as initialVirtualCardsState,
  VirtualCardsState,
} from 'src/redux/reducers/data/virtualCards.reducer'

export type DataState = {
  virtualCards: VirtualCardsState
  creditCards: CreditCardsState
  users: UsersState
  transactions: TransactionsState
  receiptAttachments: ReceiptAttachmentsState
  virtualCardPushTasks: VirtualCardPushTasksState
  reportDownloads: ReportDownloadsState
  statistics: StatisticsState
  contacts: ContactsState
  members: MembersState
  invitedToOrg: InvitedToOrgState
  organizations: OrganizationsState
  orgIntegrations: OrgIntegrationsState
  expenses: ExpensesState
  reactQueryInterop: Record<string, never>
}

export const initialState: DataState = {
  virtualCards: initialVirtualCardsState,
  creditCards: initialCreditCardsState,
  users: initialUsersState,
  transactions: initialTransactionsState,
  receiptAttachments: initialReceiptAttachmentsState,
  virtualCardPushTasks: initialVirtualCardPushTasksState,
  reportDownloads: initialReportDownloadsState,
  statistics: initialStatisticsState,
  contacts: initialContactsState,
  members: initialMembersState,
  invitedToOrg: initialInvitedToOrgState,
  organizations: initialOrganizationsState,
  orgIntegrations: initialOrgIntegrationsState,
  expenses: initialExpensesState,
  reactQueryInterop: {},
}

export type Actions =
  | ResetDataStateAction
  | VirtualCardsActions
  | CreditCardsActions
  | UsersActions
  | TransactionsActions
  | ReceiptAttachmentsActions
  // CSP auth payloads consumed by ReactQueryInteropReducer (the data.issuers
  // slice they used to feed migrated to React Query — EX-39746 csp issuers)
  | GetCspMeSuccessAction
  | PostCspLoginSuccessAction
  | PostCspLoginIssuerInfoSuccessAction
  | VirtualCardPushTasksActions
  | ReportDownloadsActions
  | StatisticsActions
  | ContactsActions
  | MembersActions
  | InvitedToOrgActions
  | OrganizationsActions
  | OrgIntegrationsActions
  | ExpensesActions

const DataReducer = (state = initialState, action: Actions): DataState =>
  action.type === RESET_DATA_STATE
    ? initialState
    : {
        virtualCards: VirtualCardsReducer(
          state.virtualCards,
          action as VirtualCardsActions,
        ),
        creditCards: CreditCardsReducer(
          state.creditCards,
          action as CreditCardsActions,
        ),
        users: UsersReducer(state.users, action as UsersActions),
        transactions: TransactionsReducer(
          state.transactions,
          action as TransactionsActions,
        ),
        receiptAttachments: ReceiptAttachmentsReducer(
          state.receiptAttachments,
          action as ReceiptAttachmentsActions,
        ),
        virtualCardPushTasks: VirtualCardPushTasksReducer(
          state.virtualCardPushTasks,
          action as VirtualCardPushTasksActions,
        ),
        reportDownloads: ReportDownloadsReducer(
          state.reportDownloads,
          action as ReportDownloadsActions,
        ),
        statistics: StatisticsReducer(
          state.statistics,
          action as StatisticsActions,
        ),
        contacts: ContactsReducer(state.contacts, action as ContactsActions),
        members: MembersReducer(state.members, action as MembersActions),
        invitedToOrg: InvitedToOrgReducer(
          state.invitedToOrg,
          action as InvitedToOrgActions,
        ),
        organizations: OrganizationsReducer(
          state.organizations,
          action as OrganizationsActions,
        ),
        orgIntegrations: OrgIntegrationsReducer(
          state.orgIntegrations,
          action as OrgIntegrationsActions,
        ),
        expenses: ExpensesReducer(state.expenses, action as ExpensesActions),
        reactQueryInterop: ReactQueryInteropReducer({}, action),
      }

export default DataReducer
